/**
 * The invite ticket's wiring: the seat read, the roster read behind the limit panel, the one write,
 * and which of three faces the card is showing.
 *
 * It decides nothing. `entities/client`'s `seatUsage` reads the seat response, `model/inviteForm`
 * validates and maps refusals, `model/sentInvitation` says which of the confirmation's cards this
 * invitation earns, and `entities/client` owns the roster projection the limit panel lists.
 */

import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  rosterQueryKey,
  rosterQueryOptions,
  seatUsageQueryKey,
  seatUsageQueryOptions,
  buildClientRoster,
  orderClientRows,
  seatUsage,
  type ClientRow,
  type SeatUsage,
} from '~/entities/client';
import { postCustomerInvite } from '~/shared/api/generated/endpoints/customer/customer';
import type { TrainWithMeModulesClientsDtosResponsesGetCustomerResponse as GetCustomerResponse } from '~/shared/api/generated/model';
import { ApiError } from '~/shared/api/mutator';

import type { SentInvitation } from '../model/sentInvitation';
import {
  EMPTY_INVITE_DRAFT,
  NO_INVITE_ERRORS,
  firstInviteError,
  inviteRefusal,
  inviteRequest,
  validateInvite,
  type InviteDraft,
  type InviteErrors,
} from '../model/inviteForm';

/** Which of KANON 02's three cards is on screen. */
export type InviteFace = 'form' | 'seatLimit' | 'done';

export interface InviteClientState {
  readonly face: InviteFace;
  readonly draft: InviteDraft;
  readonly errors: InviteErrors;
  /** A refusal that belongs to the whole form rather than to one field. An i18n key. */
  readonly formErrorKey: string | null;
  readonly seats: SeatUsage | null;
  /** The seat state as it stands AFTER the invitation, for the confirmation card. */
  readonly seatsAfter: SeatUsage | null;
  /** Who is holding the seats. Only read when the limit panel is showing. */
  readonly occupants: readonly ClientRow[];
  /**
   * Who was invited, frozen at the moment the write succeeded — `null` on every other face.
   *
   * Frozen rather than read off the draft for the reason `seatsAfter` is: the confirmation is a
   * page of prose about one person, and nothing on it may change under a trainer who is reading it.
   * `at` in particular has to be the moment it happened rather than the moment of a later render,
   * or the timeline's first row would tick forward while they look at it.
   */
  readonly sent: SentInvitation | null;
  readonly submitting: boolean;
  readonly setField: (field: keyof InviteDraft, value: string) => void;
  readonly submit: () => void;
  readonly reset: () => void;
}

export interface InviteClientOptions {
  /** Polish collation for the occupant list. */
  readonly locale: string;
  /** The dialog is showing. Both reads are gated on it. */
  readonly open: boolean;
  /**
   * A submission was refused and one field is why.
   *
   * Called on both refusal paths — the form's own validation and the server's `duplicate_email` —
   * because they are the same event to the trainer and neither of them moves focus on its own. The
   * dialog answers it by focusing that field; nothing here touches the DOM.
   *
   * Not called for a refusal that belongs to no field: the seat limit swaps the whole card, and a
   * generic 400 is announced by the form's own `role="alert"`.
   */
  readonly onRefused: (field: keyof InviteDraft) => void;
  /** What the form opens with, and returns to on „Zaproś kolejną osobę". Empty when absent. */
  readonly prefill?: InviteDraft | undefined;
  /**
   * The write. `POST /customer/invite` when absent; the public form's lead conversion passes its
   * own, which answers with the same response and refuses a full plan the same way, so every face
   * after the write is this hook's unchanged.
   */
  readonly submit?: InviteSubmit | undefined;
}

type InviteRequestBody = ReturnType<typeof inviteRequest>;

/** The invitation write, as a host may replace it. */
export type InviteSubmit = (body: InviteRequestBody) => Promise<GetCustomerResponse>;

export function useInviteClient({
  locale,
  open,
  onRefused,
  prefill,
  submit: write = postCustomerInvite,
}: InviteClientOptions): InviteClientState {
  const queryClient = useQueryClient();
  const startingDraft = prefill ?? EMPTY_INVITE_DRAFT;

  const [draft, setDraft] = useState<InviteDraft>(startingDraft);
  const [errors, setErrors] = useState<InviteErrors>(NO_INVITE_ERRORS);
  const [formErrorKey, setFormErrorKey] = useState<string | null>(null);
  const [face, setFace] = useState<InviteFace>('form');
  const [sent, setSent] = useState<SentInvitation | null>(null);
  // Frozen at the moment the write succeeded, not derived on every render: the seat read is
  // invalidated in the same breath, so a derived value would show the pre-invite count until the
  // refetch landed and then quietly correct itself under the trainer's eyes.
  const [seatsAfter, setSeatsAfter] = useState<SeatUsage | null>(null);

  // Both reads are gated on `open`: the dialog is mounted only while it is showing, but the roster
  // is a cache the roster screen already filled, and the seat count is the one thing this surface
  // must not show stale.
  const [seatQuery, rosterQuery] = useQueries({
    queries: [
      { ...seatUsageQueryOptions(), enabled: open },
      { ...rosterQueryOptions(), enabled: open },
    ],
  });

  const seats = seatUsage(seatQuery.data);
  const fold = buildClientRoster(rosterQuery.data, null);

  const invite = useMutation({
    mutationFn: (body: InviteRequestBody) => write(body),
  });

  const submit = () => {
    // A second press while the first request is in flight creates a SECOND client: the endpoint
    // provisions immediately and has no idempotency key, so two identical bodies are two people
    // holding two seats. The guard lives here rather than only on the button because the form can
    // also be submitted with Enter, and both paths land on this function.
    if (invite.isPending) return;

    const found = validateInvite(draft);
    setErrors(found);
    setFormErrorKey(null);
    const toFix = firstInviteError(found);
    if (toFix !== null) {
      onRefused(toFix);
      return;
    }

    // Refused before the request when the seat count already says there is no room. The backend
    // checks again inside a serializable transaction — this only saves a round trip and lets the
    // panel explain itself rather than reporting a failure.
    if (seats !== null && !seats.canInvite) {
      setFace('seatLimit');
      return;
    }

    const body = inviteRequest(draft);
    invite.mutate(body, {
      onSuccess: (created) => {
        setSent({
          ...body,
          clientId: created.id ?? null,
          existingAccount: created.hasOpenEngagement === false,
          at: new Date(),
        });
        // `hasOpenEngagement` is the ONE field that tells the endpoint's two paths apart, and it
        // decides half of what the confirmation may say — see `model/sentInvitation`. `=== false`
        // rather than `!`: the field is optional on the wire and an absent one must not be read as
        // "this person already had an account".
        //
        // `seatQuery.data` is still the PRE-invite response here — it was invalidated in the same
        // breath and has not come back — so the seat this write just consumed is the one the
        // response does not know about. `extraTaken` adds it, and freezing the projection here is
        // what stops the refetch from counting it twice.
        setSeatsAfter(seatUsage(seatQuery.data, { extraTaken: 1 }));
        setFace('done');
        void queryClient.invalidateQueries({ queryKey: rosterQueryKey });
        void queryClient.invalidateQueries({ queryKey: seatUsageQueryKey });
      },
      onError: (error: unknown) => {
        const status = error instanceof ApiError ? error.status : 0;
        const code = error instanceof ApiError ? error.code : undefined;
        const refusal = inviteRefusal(status, code);
        if (refusal.kind === 'seatLimit') {
          setFace('seatLimit');
          return;
        }
        if (refusal.kind === 'field') {
          setErrors({ ...NO_INVITE_ERRORS, email: refusal.messageKey });
          onRefused(refusal.field);
          return;
        }
        setFormErrorKey(refusal.messageKey);
      },
    });
  };

  return {
    face,
    draft,
    errors,
    formErrorKey,
    seats,
    seatsAfter,
    occupants: orderClientRows(
      fold.rows.filter((row) => row.status === 'active' || row.status === 'invited'),
      'name',
      locale,
    ),
    sent,
    submitting: invite.isPending,
    setField: (field, value) => {
      setDraft((current) => ({ ...current, [field]: value }));
      // The field's complaint is cleared as soon as it is edited — including the server's
      // `duplicate_email`, which is about the address that WAS typed, not the one being typed now.
      setErrors((current) => ({ ...current, [field]: null }));
      setFormErrorKey(null);
    },
    submit,
    reset: () => {
      setDraft(startingDraft);
      setErrors(NO_INVITE_ERRORS);
      setFormErrorKey(null);
      setSent(null);
      setSeatsAfter(null);
      setFace('form');
    },
  };
}
